import {
  FilterDropdownTextItem,
  Loader,
  Select,
  SelectItem,
  SelectSearch,
} from '@energybox/react-ui-library/dist/components';
import { Equipment } from '@energybox/react-ui-library/dist/types';
import { hasSubstr, isDefined } from '@energybox/react-ui-library/dist/utils';

import React from 'react';
import { IoIosAddCircle, IoIosRemoveCircle } from 'react-icons/io';
import { connect } from 'react-redux';
import {
  getEquipmentBySiteId,
  showNewEquipmentModal,
} from '../../actions/equipment';
import { ApplicationState } from '../../reducers';
import styles from './Selects.module.css';

const NO_EQUIPMENT_TEXT = 'No Equipment';

interface OwnProps {
  onCreateEquipmentModalOpen?: () => void;
  onSelect: (equipmentId: number | null) => void;
  onLoad?: () => void;
  value?: number | null;
  siteId: number;
  disabled?: boolean;
  error?: boolean;
  noBottomLine?: boolean;
  customErrorText?: string;
  isSuperHubPage?: boolean;
  preSelectedEquipment?: Equipment;
  includeNullSelectOption?: boolean;
}

interface Props extends OwnProps {
  getEquipmentBySiteId: typeof getEquipmentBySiteId;
  showNewEquipmentModal: typeof showNewEquipmentModal;
  equipments: Equipment[];
  isEquipmentLoaded: boolean;
}

interface State {
  search: string;
}

class SelectEquipment extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      search: '',
    };
  }

  searchChange(e: React.FormEvent<HTMLInputElement>) {
    this.setState({
      search: e.currentTarget.value,
    });
  }

  resetSearch() {
    this.setState({ search: '' });
  }

  loadEquipment() {
    const { getEquipmentBySiteId, siteId, isEquipmentLoaded } = this.props;
    if (this.isSiteSelected() && !isEquipmentLoaded) {
      getEquipmentBySiteId(siteId);
    }
  }

  componentDidMount() {
    this.loadEquipment();
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    if (this.props.siteId !== prevProps.siteId) {
      this.loadEquipment();
    }
  }

  filteredEquipment = (equipments: Equipment[]) => {
    const { search } = this.state;

    return equipments.filter((equipment: Equipment) => {
      if (search.length <= 2) return true;
      return hasSubstr(equipment.title, search);
    });
  };

  isSiteSelected = () => {
    if (isDefined(this.props.siteId) && this.props.siteId !== -1) {
      return true;
    }
    return false;
  };

  render() {
    const {
      equipments,
      value,
      onSelect,
      disabled,
      error,
      customErrorText,
      noBottomLine = false,
      showNewEquipmentModal,
      onCreateEquipmentModalOpen,
      isSuperHubPage,
      preSelectedEquipment,
      includeNullSelectOption,
      isEquipmentLoaded,
    } = this.props;

    if (isSuperHubPage && preSelectedEquipment) {
      return (
        <Select
          variant={'select'}
          disabled={true}
          title={preSelectedEquipment ? preSelectedEquipment.title : undefined}
          value={preSelectedEquipment.id}
          error={error}
          customErrorText={customErrorText}
          noBottomLine={noBottomLine}
          className={styles.selectButtonText}
        >
          <SelectItem isSelected={true} onSelect={() => {}}>
            {preSelectedEquipment.title}
          </SelectItem>
        </Select>
      );
    }

    if (this.isSiteSelected() && !isEquipmentLoaded) {
      return <Loader size={12} />;
    } else {
      const { search } = this.state;
      const selectedEquipment = equipments.find(
        equipment => equipment.id === value
      );
      const filteredEquipment = this.filteredEquipment(equipments);
      const isNoEquipmentOptionSelected = value === null || value === undefined;

      return (
        <Select
          variant={'select'}
          disabled={!this.isSiteSelected() || disabled}
          onClick={this.resetSearch.bind(this)}
          title={selectedEquipment ? selectedEquipment.title : undefined}
          value={
            isNoEquipmentOptionSelected ? undefined : selectedEquipment?.id
          }
          error={error}
          customErrorText={customErrorText}
          noBottomLine={noBottomLine}
          className={styles.selectButtonText}
        >
          <SelectSearch
            onChange={this.searchChange.bind(this)}
            value={search}
            error={filteredEquipment.length === 0}
          />
          {includeNullSelectOption && !isNoEquipmentOptionSelected && (
            <FilterDropdownTextItem
              title={
                <>
                  <IoIosRemoveCircle size='20' color='var(--pink-base)' />
                  <span
                    className={styles.equipmentText}
                    style={{ color: 'var(--pink-base)' }}
                  >
                    {NO_EQUIPMENT_TEXT}
                  </span>
                </>
              }
              key={'None'}
              onClick={() => onSelect(null)}
              closeOnClick
            />
          )}
          {onCreateEquipmentModalOpen && (
            <FilterDropdownTextItem
              title={
                <>
                  <IoIosAddCircle size='20' />
                  <span className={styles.equipmentText}>
                    Add New Equipment
                  </span>
                </>
              }
              key={'Add New Equipment'}
              onClick={() => {
                onCreateEquipmentModalOpen();
                showNewEquipmentModal();
              }}
              closeOnClick
            />
          )}
          {filteredEquipment.map((equipment: Equipment) => (
            <SelectItem
              key={equipment.id}
              isSelected={equipment.id === selectedEquipment?.id}
              onSelect={() => onSelect(equipment.id)}
            >
              {equipment.title}
            </SelectItem>
          ))}
        </Select>
      );
    }
  }
}

const mapStateToProps = (
  { equipment }: ApplicationState,
  { siteId }: OwnProps
) => {
  const equipmentIds = equipment.equipmentIdsBySiteId[siteId];

  return {
    isEquipmentLoaded: equipmentIds !== undefined,
    equipments: (equipmentIds || []).map(id => equipment.equipmentById[id]),
  };
};

const mapDispatchToProps = {
  getEquipmentBySiteId,
  showNewEquipmentModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectEquipment);
